import { Links, Meta, useLoaderData, useLocation } from 'react-router';
import { AdmiralCmp } from '@cx-web/components/third-party/admiral-cmp';
import { GooglePublisherTags } from '@cx-web/components/third-party/google-publisher-tags';
import { GoogleTagManagerHead } from '@cx-web/components/third-party/google-tag-manager';
import { VwoAnalytics } from '@cx-web/components/third-party/vwo';
import { REPUB_CONFIG } from '@cx-web/repub-config';
import { ClientOnly } from 'remix-utils/client-only';

import { featureFlags, useFeatureFlag } from '~/providers/feature-flag-provider';
import { useRepubContext } from '~/providers/repub-context';
import type { FontAttribs } from '~/types';

import { type loader } from './loader.server';

export const Favicon = ({ resourcesUrl }: { resourcesUrl: string }) => {
  const faviconPath = `${resourcesUrl}/assets/img/`;
  const { repub } = useRepubContext();

  const faviconFile = REPUB_CONFIG[repub]?.favicon || 'oc-favicon.svg';
  const favicon = `${faviconPath}${faviconFile}`;

  return (
    <>
      <link as="image" href={favicon} key={`preload-${favicon}`} rel="preload" />
      <link href={favicon} key={`icon-${favicon}`} rel="icon" />
    </>
  );
};

export const Fonts = () => {
  const baseFonts: FontAttribs[] = [
    // Public Sans, Open Sans, Lato
    {
      rel: 'preconnect',
      href: 'https://fonts.googleapis.com',
    },
    {
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous',
    },
    // Futura PT Condensed
    {
      rel: 'preconnect',
      href: 'https://use.typekit.net',
    },
  ];

  return (
    <>
      {baseFonts.map(data => (
        <link key={`${data.rel}-${data.href}`} {...data} />
      ))}
    </>
  );
};

export const LcpImageLinks = ({ preloadLinks }: { preloadLinks?: string[] }) => {
  if (!preloadLinks || preloadLinks.length === 0) {
    return null;
  }

  return (
    <>
      {preloadLinks.map((imageUrl, index) => (
        <link
          as="image"
          // @ts-expect-error - fetchpriority is valid HTML but not in React 18 types
          fetchpriority="high"
          href={imageUrl}
          key={`lcp-preload-${index}`}
          rel="preload"
        />
      ))}
    </>
  );
};

export default function Head() {
  const rootLoaderData = useLoaderData<typeof loader>();
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const { pathname } = useLocation();
  const disableGtm = useFeatureFlag(featureFlags.DISABLE_GOOGLE_TAG_MANAGER);
  const disableVwo = useFeatureFlag(featureFlags.DISABLE_VWO);
  const shouldRenderGTMPostHydration = useFeatureFlag(featureFlags.RENDER_GTM_POST_HYDRATION);

  const {
    ENV,
    admiralCmpUrl,
    baseUrl,
    cssHrefs,
    geolocationInfo,
    gtmId,
    isAdmiralCookieBannerEnabled,
    myocData,
    preloadLinks,
    resourcesUrl,
  } = rootLoaderData;

  const { repub, repubIs } = useRepubContext();

  return (
    <head>
      <meta charSet="utf-8" />
      <meta content="width=device-width,initial-scale=1" name="viewport" />
      <meta content={`app-id=6459478704, app-argument=${pathname}`} name="apple-itunes-app" />
      <meta content="telephone=no" name="format-detection" />
      <base href={baseUrl} />
      <LcpImageLinks preloadLinks={preloadLinks} />
      <Meta />
      <AdmiralCmp admiralUrl={admiralCmpUrl} enabled={isAdmiralCookieBannerEnabled} />
      {isAdmiralCookieBannerEnabled && <link as="script" href={admiralCmpUrl} rel="preload" />}
      <link
        as="image"
        // @ts-expect-error - fetchpriority is valid HTML but not in React 18 types
        fetchpriority="high"
        href={`${resourcesUrl}/assets/img/OC_LOGO_2.webp?w=213&h=34&fm=webp&q=80`}
        rel="preload"
      />
      <Links />
      {cssHrefs.map(href => (
        <link href={href} key={href} rel="stylesheet" />
      ))}
      <Favicon resourcesUrl={resourcesUrl} />
      <Fonts />
      <GooglePublisherTags />
      <GoogleTagManagerHead
        geolocationInfo={geolocationInfo}
        gtmId={gtmId}
        repub={repub}
        shouldRender={!disableGtm && (ENV.ENVIRONMENT === 'PROD' || ENV.ENVIRONMENT === 'STG')}
        shouldRenderPostHydration={shouldRenderGTMPostHydration}
        userGroup={myocData?.userGroup}
        userId={myocData?.myOCUser?.userID}
      />
      <ClientOnly fallback={null}>
        {() => <VwoAnalytics accountId={repubIs.US ? 780115 : 772951} disabled={disableVwo} />}
      </ClientOnly>
      {ENV.ENVIRONMENT !== 'LOCALHOST' && (
        <>
          <script defer src={`${resourcesUrl.replace('/public', '')}/fingerprint/verify-client.js`} />
          <script defer src={`${resourcesUrl.replace('/public', '')}/nirvana/ping.js`} />
        </>
      )}
    </head>
  );
}
